import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UserCheck, UserPlus } from "lucide-react";
import type { FollowResponse } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { json, webApi } from "@web-shared/lib/web-api";
import { useAuthGate } from "../auth";

/** Follow / Following. Signed out, it asks you to sign in and then follows. */
export function FollowButton({
  handle,
  name,
  following,
  onChange,
  quiet = false,
  iconOnly = false,
}: {
  handle: string;
  name: string;
  following: boolean;
  onChange?: (res: FollowResponse) => void;
  /** Outlined even when not following — for places that already have a
   * primary button (the seed hero, where "Ableton" is the loud one). */
  quiet?: boolean;
  /** Render as an icon-only button (mobile profile). */
  iconOnly?: boolean;
}) {
  const gate = useAuthGate();
  const queryClient = useQueryClient();
  const [state, setState] = useState(following);
  const [busy, setBusy] = useState(false);
  useEffect(() => setState(following), [following]);

  const toggle = () =>
    gate.requireSignIn("follow", `Sign in to follow ${name}`, () => {
      if (busy) return;
      const next = !state;
      setState(next);
      setBusy(true);
      webApi<FollowResponse>(`/users/${encodeURIComponent(handle)}/follow`, json(next ? "POST" : "DELETE"))
        .then((res) => {
          setState(res.following);
          onChange?.(res);
          // Their profile counts, and your Following tab, just changed.
          void queryClient.invalidateQueries({ queryKey: ["web", "profile"] });
          void queryClient.invalidateQueries({ queryKey: ["web", "feed", "following"] });
        })
        .catch(() => setState(!next))
        .finally(() => setBusy(false));
    });

  if (iconOnly) {
    return (
      <Button
        size="icon"
        variant="ghost"
        className={`h-8 w-8 rounded-lg border ${state ? "border-primary text-primary" : "border-strong"}`}
        onClick={toggle}
        aria-pressed={state}
        title={state ? `Unfollow ${name}` : `Follow ${name}`}
        aria-label={state ? "Following" : "Follow"}
      >
        {state ? <UserCheck className="size-4" /> : <UserPlus className="size-4" />}
      </Button>
    );
  }

  return (
    <Button size="sm" variant={state || quiet ? "outline" : "default"} onClick={toggle} aria-pressed={state}>
      {state ? "Following" : "Follow"}
    </Button>
  );
}
